export type BillFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly' | 'one_off';
export type BillStatus = 'upcoming' | 'due' | 'overdue' | 'paid';
export type BillSource = 'manual' | 'scan';

export interface Bill {
  id: string;
  userId: string;
  /** Source / bank-style label (locked when source is scan) */
  name: string;
  /** User-friendly label shown on the Budgets page */
  displayName: string;
  source: BillSource;
  categoryId?: string;
  categoryName?: string;
  /** Top-level (parent) category for display chips */
  primaryCategoryName?: string;
  primaryCategoryColor?: string;
  amount: number;
  frequency: BillFrequency;
  /** Stored start/anchor date (recurring) or due date (one-off). */
  anchorDate: string;
  /** Effective next due date for display, status, and reminders. */
  dueDate: string;
  dayOfMonth?: number;
  autopay: boolean;
  status: BillStatus;
  statusTags: string[];
  relativeTiming: string;
  linkedTransactionId?: string;
  lastPaidAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Preferred label for UI lists and reminders. */
export function getBillDisplayName(bill: Pick<Bill, 'displayName' | 'name'>): string {
  return bill.displayName?.trim() || bill.name;
}

export interface BillSummary {
  totalMonthlyBills: number;
  billsDueSoonCount: number;
  billsOnAutopayCount: number;
}

export interface QuickReminder {
  billId: string;
  name: string;
  amount: number;
  dueDate: string;
  severity: 'warning' | 'critical';
  message: string;
  primaryAction: 'pay_now' | 'schedule' | 'review';
  autopay?: boolean;
}

export interface BillReminderPayload {
  id: string;
  name: string;
  amount: number;
  dueDate: string;
  message: string;
  severity: 'info' | 'warning' | 'critical';
  autopay: boolean;
}

export interface CreateBillInput {
  name: string;
  displayName?: string;
  source?: BillSource;
  categoryId?: string;
  amount: number;
  frequency: BillFrequency;
  dueDate: string;
  dayOfMonth?: number;
  autopay?: boolean;
}

export interface UpdateBillInput extends Partial<CreateBillInput> {}

export interface BillServiceResponse<T> {
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}
